import axios, { AxiosResponse } from 'axios';
import type { SharedScheduleWriteContext } from '../features/sharedMembership';

export type ScheduleHistoryRange = 'day' | 'week' | 'month';

/**
 * 일반일정 반복 등록 규칙.
 * localDate / startTime / endTime 은 모두 KST 벽시계 값이며, 실제 날짜 전개는 서버가 KST 기준으로 수행한다.
 * (프론트는 scheduleTimes 배열을 만들어 보내지 않는다)
 */
export type RepeatGeneralScheduleRecurrence = {
    frequency: 'DAILY' | 'WEEKLY';
    count: number; // 1~200, 최초 회차 포함
    localDate: string; // 'YYYY-MM-DD' (KST)
    startTime: string; // 'HH:mm' (KST)
    endTime: string; // 'HH:mm' (KST)
};

/**
 * 듀엣(1:2) 실패 계약 선언. **`POST .../duplicable` 요청에만 싣는다.**
 *
 * 서버는 이 헤더를 "클라이언트가 아래 셋을 전부 한다"는 선언으로 읽고, 그때만 서로 다른
 * 공유 풀 두 개를 허용한다. 선언이 없으면 예전처럼 409 SHARED_MEMBERSHIP_MULTI_POOL_UNSUPPORTED
 * 로 거절한다(구버전 웹 탭 보호).
 *
 *  1. 실패 응답의 `details.retryPolicy` 로 요청번호 폐기를 판정한다
 *     → features/sharedMembership/errors.ts 의 shouldDiscardSharedScheduleOperation
 *  2. `partialWriteMayExist` 와 무관하게 **언제나** 일정 목록을 재조회한다
 *     → components/CreateSchedule/scheduleWriteCache.ts 의 refreshScheduleWriteQueries
 *  3. 자동 재시도를 하지 않는다 — 사람이 목록을 보고 판단한다
 *
 * ⚠️ **셋 중 하나라도 되돌리면 이 선언이 거짓이 된다.** 그때는 이 헤더를 먼저 빼야 한다.
 * 헤더를 단 채로 위 동작을 없애면, 서버는 다중 풀을 열어 주는데 클라이언트는 부분 성공을
 * 처리하지 못하는 상태가 되어 중복 일정이 조용히 생긴다.
 *
 * 이름·값이 틀려도 에러가 아니라 그냥 409 로 떨어져 원인이 드러나지 않는다. 그래서
 * schedule.sharedMembership.test.ts 가 리터럴로 잠근다.
 * (선례: coach.controller.js 의 x-rappo-invitation-flow: v2)
 */
const DUET_FAILURE_CONTRACT_HEADER = 'x-rappo-duet-failure-contract';
const DUET_FAILURE_CONTRACT_VERSION = 'v1';

const getScheduleWriteHeaders = (
    accessToken: string,
    sharedMembership?: SharedScheduleWriteContext,
    options?: {
        /** duplicable(1:2) 전용. 다른 일정 쓰기에는 절대 켜지 않는다. */
        declareDuetFailureContract?: boolean;
    },
) => ({
    authorization: accessToken,
    ...(sharedMembership
        ? { 'Idempotency-Key': sharedMembership.operationId }
        : {}),
    ...(options?.declareDuetFailureContract
        ? { [DUET_FAILURE_CONTRACT_HEADER]: DUET_FAILURE_CONTRACT_VERSION }
        : {}),
});

// export /**
//  * @method get
//  * @brief 일간 스케쥴 조회 API
//  */
// const getDailyScheduleAPI = async (centerId: string, date: string, accessToken: string): Promise<any> => {
//     let response = {} as AxiosResponse<any, any>;
//     try {
//         response = await axios({
//             method: 'get',
//             url: `${process.env.REACT_APP_SERVER}/v1/centers/daily-schedules/${centerId}?date=${date}`,
//             headers: { authorization: accessToken },
//         });

//         if (response.status === 200) {
//             return response;
//         }
//     } catch (error: any) {
//         // Error 😨
//         switch (error.response.status) {
//             case 400:
//                 return { status: error.response.status };
//             case 401:
//                 return { status: error.response.status };
//             case 403:
//                 return { status: error.response.status };
//             case 404:
//                 return { status: error.response.status };
//             case 500:
//                 alert('서버 내부 오류.');
//                 return { status: error.response.status };
//         }
//     }
// };

export /**
 * @method get
 * @brief 일간 스케쥴 조회 v3 API
 */
const getDailyScheduleAPI = async (centerId: string, date: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v4/centers/daily-schedules/${centerId}?date=${date}`,

            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 주간 스케쥴 조회 API
 */
const getWeeklyScheduleAPI = async (
    centerId: string,
    date: string,
    selectedCoachId: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        const params = new URLSearchParams();
        params.append('date', String(date));
        if (selectedCoachId) {
            params.append('selectedCoachId', String(selectedCoachId));
        }
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v4/centers/weekly-schedules/${centerId}?${params.toString()}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 목록 화면 날짜 범위 스케쥴 조회 API
 */
const getRangeScheduleAPI = async (
    centerId: string,
    startDate: string,
    endDate: string,
    selectedCoachId: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        const params = new URLSearchParams();
        params.append('startDate', String(startDate));
        params.append('endDate', String(endDate));
        if (selectedCoachId) {
            params.append('selectedCoachId', String(selectedCoachId));
        }
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v4/centers/range-schedules/${centerId}?${params.toString()}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 스케줄변경/취소탭 ->  당일 취소및 변경된 수업 횟수 조회 API
 */
const getCancelAndChangeCountAPI = async (centerId: string, date: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/today-adjustment-count/${centerId}?date=${date}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 스케줄변경/취소탭 ->  당일 취소및 변경 리스트 조회 API
 */
const getCancelAndChangeListAPI = async (
    centerId: string,
    date: string,
    accessToken: string,
    range?: ScheduleHistoryRange
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        const params = new URLSearchParams();
        params.append('date', date);
        if (range) {
            params.append('range', range);
        }
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/today-adjustment-list/${centerId}?${params.toString()}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 스케줄변경/취소탭 -> 회원의 수업취소 리스트 조회 API
 */
const getMemberCancelScheduleListAPI = async (
    centerId: string,
    date: string,
    accessToken: string,
    range?: ScheduleHistoryRange
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        const params = new URLSearchParams();
        params.append('date', date);
        if (range) {
            params.append('range', range);
        }
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/member-cancel-list/${centerId}?${params.toString()}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief 일간 강사스케줄 / 일반일정 등록
 */
const createGeneralScheduleAPI = async (
    centerId: string,
    coachId: string,
    name: string,
    startTime: string,
    endTime: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/onetimeschedules/center/${centerId}`,
            headers: { authorization: accessToken },
            data: {
                coachId,
                name,
                startTime,
                endTime,
            },
        });

        if (response.status === 201) {
            return response;
        }
        return { status: response.status, data: response.data };
    } catch (error: any) {
        // Error 😨
        // 네트워크 오류(응답 자체가 없는 경우)에 error.response.status 접근으로 2차 예외가 나지 않도록 방어한다.
        const status = error?.response?.status ?? 0;
        switch (status) {
            case 400:
                return { status, data: error?.response?.data };
            case 401:
                return { status, data: error?.response?.data };
            case 403:
                return { status, data: error?.response?.data };
            case 404:
                return { status, data: error?.response?.data };
            case 406:
                return { status, data: error?.response?.data };
            case 410:
                return { status, data: error?.response?.data };
            case 420:
                return { status, data: error?.response?.data };
            case 430:
                return { status, data: error?.response?.data };
            case 500:
                alert('서버 내부 오류.');
                return { status, data: error?.response?.data };
            default:
                return { status, data: error?.response?.data };
        }
    }
};

export /**
 * @method post
 * @brief 일간 강사스케줄 / 일반일정 반복 등록
 * @description
 *  - 서버가 KST 기준으로 recurrence 를 전개하므로 프론트는 scheduleTimes 배열을 만들지 않는다.
 *  - operationId 는 클라이언트가 만드는 멱등키다. 같은 등록 시도의 재시도에서는 같은 값을 보내야 한다.
 */
const createRepeatGeneralScheduleAPI = async (
    centerId: string,
    coachId: string,
    name: string,
    operationId: string,
    recurrence: RepeatGeneralScheduleRecurrence,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/onetimeschedules/center/${centerId}/repeat`,
            headers: { authorization: accessToken },
            data: {
                coachId,
                name,
                operationId,
                recurrence,
            },
        });

        if (response.status === 201) {
            return response;
        }
        return { status: response.status, data: response.data };
    } catch (error: any) {
        // Error 😨
        // 네트워크 오류(응답 자체가 없는 경우)에 error.response.status 접근으로 2차 예외가 나지 않도록 방어한다.
        const status = error?.response?.status ?? 0;
        switch (status) {
            case 400: // 시간/규칙 오류, 개수 초과, 기간 초과, 배치 내부 겹침
                return { status, data: error?.response?.data };
            case 401:
                return { status, data: error?.response?.data };
            case 403:
                return { status, data: error?.response?.data };
            case 404: // 센터에 해당 강사 없음
                return { status, data: error?.response?.data };
            case 406: // 권한 없음
                return { status, data: error?.response?.data };
            case 410: // 기존 일정과 중복
                return { status, data: error?.response?.data };
            case 420: // 기존 일정과 중복
                return { status, data: error?.response?.data };
            case 430: // 기존 일정과 중복
                return { status, data: error?.response?.data };
            case 500:
                alert('서버 내부 오류.');
                return { status, data: error?.response?.data };
            default:
                return { status, data: error?.response?.data };
        }
    }
};

export /**
 * @method delete
 * @brief 일간 강사스케줄 / 일반일정 반복(번들) 삭제
 * @description
 *  취소 범위는 `anchor.startTime` 이후의 같은 번들 일정이며, anchor 회차 자신도 포함된다.
 *  기준은 "지금"이 아니라 "anchor 로 고른 그 회차"다. 이미 지나간 회차를 anchor 로 고르면
 *  그 회차부터 함께 취소된다. anchor 보다 앞선 회차만 남는다.
 */
const deleteRepeatGeneralScheduleAPI = async (
    centerId: string,
    onetimeScheduleId: string,
    scheduleBundleId: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'delete',
            url: `${process.env.REACT_APP_SERVER}/v1/onetimeschedules/center/${centerId}/repeat?onetimeScheduleId=${encodeURIComponent(
                onetimeScheduleId
            )}&scheduleBundleId=${encodeURIComponent(scheduleBundleId)}`,
            headers: { authorization: accessToken },
        });

        // 확정 성공 코드는 200. 204 도 성공으로 받아들인다.
        if (response.status === 200 || response.status === 204) {
            return response;
        }
        return { status: response.status, data: response.data };
    } catch (error: any) {
        // Error 😨
        // 네트워크 오류(응답 자체가 없는 경우)에 error.response.status 접근으로 2차 예외가 나지 않도록 방어한다.
        const status = error?.response?.status ?? 0;
        switch (status) {
            case 400:
                return { status, data: error?.response?.data };
            case 401:
                return { status, data: error?.response?.data };
            case 403:
                return { status, data: error?.response?.data };
            case 404:
                return { status, data: error?.response?.data };
            case 406:
                return { status, data: error?.response?.data };
            case 500:
                alert('서버 내부 오류.');
                return { status, data: error?.response?.data };
            default:
                return { status, data: error?.response?.data };
        }
    }
};

export /**
 * @method patch
 * @brief 일간 강사스케줄 / 일반일정 수정
 */
const modifyGeneralScheduleAPI = async (
    centerId: string,
    onetimeScheudleId: string,
    coachId: string,
    name: string,
    startTime: string,
    endTime: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/onetimeschedules/center/${centerId}/onetimeScheudle/${onetimeScheudleId}`,
            headers: { authorization: accessToken },
            data: {
                coachId,
                name,
                startTime,
                endTime,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 410:
                return { status: error.response.status };
            case 420:
                return { status: error.response.status };
            case 430:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

// 스케줄 생성 관련

export /**
 * @method get
 * @brief 강사 리스트 드롭다운 에서 coachName, coachId 조회 api
 */
const getCoachListAPI = async (centerId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/coach-name-list/${centerId}`,

            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method get
 * @brief 센터 스케줄 생성을 위한 강사의 회원 리스트 조회
 */
const getMemberListForCenterAPI = async (centerId: string, coachId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v2/rapports/center/${centerId}/coach/${coachId}/member-list-for-create-schedule`,

            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief 1:1 일정추가
 */
const createOneOnOneScheduleAPI = async (
    centerId: string,
    coachId: string,
    memberId: string,
    startTime: string,
    endTime: string,
    membershipId: string,
    accessToken: string,
    sharedMembership?: SharedScheduleWriteContext,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}`,
            headers: getScheduleWriteHeaders(accessToken, sharedMembership),
            data: {
                coachId,
                memberId,
                startTime,
                endTime,
                membershipId,
                ...(sharedMembership || {}),
            },
        });

        return response;
    } catch (error: any) {
        // Error 😨 — 어떤 실패든 { status }를 반환해 호출부 onSuccess에서 TypeError 없이 처리되게 한다
        const status = error?.response?.status ?? 0;
        if (status === 500) {
            alert('서버 내부 오류.');
        }
        return { status, data: error?.response?.data };
    }
};

export /**
 * @method post
 * @brief 1:2 일정추가
 */
const createOneOnTwoScheduleAPI = async (
    centerId: string,
    coachId: string,
    scheduleTimes: any,
    accessToken: string,
    /** 공유 수강권이 섞인 1:2는 풀 단위로 회차를 한꺼번에 뺀다. 재시도 멱등키가 필요하다. */
    sharedMembership?: SharedScheduleWriteContext
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}/duplicable`,
            // 공유 문맥이 없는 요청에도 항상 선언한다. 근거는 **이 클라이언트가 세 의무를
            // 언제나 수행한다**는 것이지, 낡은 정보로도 등록되게 하려는 것이 아니다.
            //
            // 실제로 프론트가 공유인 줄 모르고 보낸 요청은 등록되지 않고 400 으로 닫힌다.
            // 웹은 membershipSharePoolId 가 없으면 operationId 자체를 만들지 않는데
            // (scheduleEntitlements.ts 의 getStableSharedScheduleOperation), 서버는 발견한
            // 풀 ID 를 sharedMembership 에 다시 주입하므로(schedule.service.js) legacy operation
            // fallback 조건이 깨지고 requireOperationId 가 400 SHARED_MEMBERSHIP_INVALID_INPUT
            // 을 던진다(sharedMembershipCapacity.service.ts). fail-closed 라 중복·정산 위험은 없다.
            headers: getScheduleWriteHeaders(accessToken, sharedMembership, {
                declareDuetFailureContract: true,
            }),
            data: {
                coachId,
                scheduleTimes,
                ...(sharedMembership?.operationId && { operationId: sharedMembership.operationId }),
            },
        });

        return response;
    } catch (error: any) {
        // Error 😨 — 어떤 실패든 { status, data }를 반환해 호출부 onSuccess에서 TypeError 없이 처리되게 한다.
        // 서버 본문을 버리면 409 SHARED_MEMBERSHIP_* 의 errorCode 가 사라져 호출부의
        // getSharedScheduleErrorMessage 가 null 을 돌려주고, 정확한 안내 대신 "서버 내부 오류"가 뜬다.
        // 1:1(createOneOnOneScheduleAPI) 과 같은 형태로 상태와 본문을 항상 그대로 넘긴다.
        return { status: error?.response?.status ?? 0, data: error?.response?.data };
    }
};

export /**
 * @method post
 * @brief 반복 일정 추가
 */
const createRepeatScheduleAPI = async (
    centerId: string,
    baseStartDate: string,
    coachId: string,
    memberId: string,
    membershipId: string,
    scheduleTimes: any,
    accessToken: string,
    sharedMembership?: SharedScheduleWriteContext,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}/repeat`,
            headers: getScheduleWriteHeaders(accessToken, sharedMembership),
            data: {
                baseStartDate,
                coachId,
                memberId,
                membershipId,
                scheduleTimes,
                ...(sharedMembership || {}),
            },
        });

        if (response.status === 200 || response.status === 201) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        const status = error?.response?.status ?? 0;
        switch (status) {
            case 400:
                return { status, data: error?.response?.data };
            case 401:
                return { status, data: error?.response?.data };
            case 403:
                return { status, data: error?.response?.data };
            case 404:
                return { status, data: error?.response?.data };
            case 406:
                return { status, data: error?.response?.data };
            case 409:
                return { status, data: error?.response?.data };
            case 410:
                return { status, data: error?.response?.data };
            case 420:
                return { status, data: error?.response?.data };
            case 430:
                return { status, data: error?.response?.data };
            case 500:
                alert('서버 내부 오류.');
                return { status, data: error?.response?.data };
            default:
                return { status, data: error?.response?.data };
        }
    }
};

export /**
 * @method post
 * @brief 반복 일정 삭제
 */
const deleteRepeatScheduleAPI = async (
    centerId: string,
    scheduleId: string,
    scheduleBundleId: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'delete',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}/repeat?scheduleId=${scheduleId}&scheduleBundleId=${scheduleBundleId}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 204) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief OFF 일정 추가
 */
const createOffAPI = async (
    centerId: string,
    coachId: string,
    blockType: string,
    startTime: string,
    endTime: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/blocks/center/${centerId}`,
            headers: { authorization: accessToken },
            data: {
                coachId,
                blockType,
                startTime,
                endTime,
            },
        });

        if (response.status === 201) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 410:
                return { status: error.response.status };
            case 420:
                return { status: error.response.status };
            case 430:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method patch
 * @brief 일간/주간 강사스케줄 / 수업일정 수정
 */
const modifyOneOnOneScheduleAPI = async (
    centerId: string,
    scheduleId: string,
    coachId: string,
    changedStartTime: string,
    changedEndTime: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}/schedule/${scheduleId}`,
            headers: { authorization: accessToken },
            data: {
                coachId,
                changedStartTime,
                changedEndTime,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // 일정 수정도 서버에서 항상 usage lease를 타므로 409(MEMBERSHIP_USAGE_BUSY)가 나올 수 있다.
        // 분기하지 않은 상태코드를 undefined로 삼키면 호출부 onSuccess의 res.status가 터져
        // 성공도 실패도 안 뜨는 무음 실패가 된다. 상태와 서버 본문을 항상 그대로 넘긴다.
        return { status: error?.response?.status ?? 0, data: error?.response?.data };
    }
};

/**
 * 빠른 「시간·날짜 변경」 전용 일정 쓰기 결과.
 *
 * 기존 수정 API 들은 실패 시 `{ status }` 만 돌려주거나(일반일정) 무응답에서 TypeError 로
 * 터져서(error.response 가 없음) 서버의 errorCode·message 를 잃는다. 빠른 변경은
 * 409 SCHEDULE_STALE·공유 수강권 오류·결과 미확인(무응답)을 구분해야 하므로 실패를
 * **던지지 않고** 항상 이 형태로 돌려준다. 무응답(타임아웃·네트워크 끊김)은 status 0.
 * 500 에서도 alert 를 띄우지 않는다 — 문구는 호출부(시트)가 결정한다.
 */
export type ScheduleWriteResult<T = any> =
    | { ok: true; status: number; data: T; staleGuardSkipped?: boolean }
    | { ok: false; status: number; errorCode: string; message: string; data: unknown; staleGuardSkipped?: boolean };

const toScheduleWriteFailure = (error: any): ScheduleWriteResult => {
    const status = typeof error?.response?.status === 'number' ? error.response.status : 0;
    const data = error?.response?.data;
    const errorCode = typeof data?.errorCode === 'string' ? data.errorCode.trim() : '';
    const message = typeof data?.message === 'string' ? data.message : '';
    return { ok: false, status, errorCode, message, data };
};

const toScheduleWriteResult = (response: AxiosResponse<any, any>): ScheduleWriteResult =>
    response && response.status >= 200 && response.status < 300
        ? { ok: true, status: response.status, data: response.data }
        : toScheduleWriteFailure({ response });

const EXPECTED_SCHEDULE_FIELDS = ['expectedStartTime', 'expectedEndTime', 'expectedAttendanceStatus'] as const;

/**
 * 서버가 expected* 를 **아직 모르는가**.
 *
 * validate.js 는 allowUnknown 없이 `Joi.object().keys()` 를 쓰므로, 이 필드를 모르는 서버는
 * `"expectedStartTime" is not allowed` 로 400 을 낸다. 신서버의 다른 400(예: `"expectedStartTime"
 * must be a valid date`, 시간 형식 오류)은 여기에 걸리지 않게 "is not allowed" 까지 함께 본다.
 */
const isExpectedScheduleFieldUnsupported = (result: ScheduleWriteResult): boolean => {
    if (result.ok || result.status !== 400) return false;
    const message = typeof result.message === 'string' ? result.message : '';
    return (
        message.includes('is not allowed') &&
        EXPECTED_SCHEDULE_FIELDS.some((field) => message.includes(`"${field}"`))
    );
};

/** 기대 상태 필드는 값이 있을 때만 싣는다. 생략 = 서버가 그 항목을 비교하지 않음. */
const hasExpectedValue = (value: string | null | undefined): value is string =>
    typeof value === 'string' && value.trim() !== '';

export type ModifyOneOnOneScheduleTimeBody = {
    coachId: string;
    changedStartTime: string;
    changedEndTime: string;
    /** 화면이 본 원본 시작 시각(ISO). 서버가 ms 단위로 비교한다. */
    expectedStartTime?: string | null;
    /** 화면이 본 원본 종료 시각(ISO). */
    expectedEndTime?: string | null;
    /**
     * 원본 **raw** attendanceStatus. null/없음이면 생략해야 한다(표시용 정규화값 금지).
     * 서버는 원본이 null/없음이면 'ATTENDANCE' 로 해석해 비교한다.
     */
    expectedAttendanceStatus?: string | null;
};

export /**
 * @method patch
 * @brief 관리자 PT 단건 시간·날짜 변경(빠른 변경). 기대 상태가 다르면 409 SCHEDULE_STALE.
 *
 * 기존 modifyOneOnOneScheduleAPI 와 같은 엔드포인트다. 그 함수의 시그니처·동작은 그대로 두고
 * (기존 수정 폼은 expected 를 보내지 않는다) 이 함수를 따로 둔다.
 * expected* 를 모르는 구서버의 unknown-key 400 에 한해 expected* 를 빼고 한 번 재시도한다(아래 주석).
 */
const modifyOneOnOneScheduleTimeAPI = async (
    centerId: string,
    scheduleId: string,
    body: ModifyOneOnOneScheduleTimeBody,
    accessToken: string
): Promise<ScheduleWriteResult> => {
    const data: Record<string, string> = {
        coachId: body.coachId,
        changedStartTime: body.changedStartTime,
        changedEndTime: body.changedEndTime,
    };
    if (hasExpectedValue(body.expectedStartTime)) data.expectedStartTime = body.expectedStartTime;
    if (hasExpectedValue(body.expectedEndTime)) data.expectedEndTime = body.expectedEndTime;
    if (hasExpectedValue(body.expectedAttendanceStatus)) {
        data.expectedAttendanceStatus = body.expectedAttendanceStatus;
    }
    const patch = async (payload: Record<string, string>): Promise<ScheduleWriteResult> => {
        try {
            const response = await axios({
                method: 'patch',
                url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}/schedule/${scheduleId}`,
                headers: { authorization: accessToken },
                data: payload,
            });
            return toScheduleWriteResult(response);
        } catch (error: any) {
            return toScheduleWriteFailure(error);
        }
    };

    const first = await patch(data);
    const sentExpected = EXPECTED_SCHEDULE_FIELDS.some((field) => field in data);
    if (!sentExpected || !isExpectedScheduleFieldUnsupported(first)) return first;

    /*
     * 기대 상태를 모르는 서버(배포 중 구 worker·서버 롤백)로 나간 경우에만, **그 필드만 빼고** 한 번 더 보낸다.
     * 선례: api/gifticon.ts sendGiftAPI 의 expectedUnitPrice 재시도.
     *
     * 이 거절은 Joi 검증 단계라 교체(신규 생성→원본 취소)가 시작되지도 않았다. 그래서 재전송해도
     * 중복이 생기지 않는다. 409·5xx·무응답은 서버에서 무언가 일어났을 수 있어 절대 재시도하지 않는다.
     * 재시도로 저장되면 stale 보호 없이 저장된 것이라 staleGuardSkipped 로 표시한다.
     */
    const withoutExpected: Record<string, string> = { ...data };
    EXPECTED_SCHEDULE_FIELDS.forEach((field) => delete withoutExpected[field]);
    const retried = await patch(withoutExpected);
    return { ...retried, staleGuardSkipped: true };
};

export type ModifyGeneralScheduleTimeBody = {
    coachId: string;
    /** 서버가 필수로 받는다. 시트를 연 시점의 원래 제목(가려진 '개인 일정'이면 이 함수를 부르면 안 된다). */
    name: string;
    startTime: string;
    endTime: string;
};

export /**
 * @method patch
 * @brief 관리자 일반일정 시간·날짜 변경(빠른 변경).
 *
 * 기존 modifyGeneralScheduleAPI 는 실패 시 status 만 돌려주고 errorCode·message 를 버린다.
 * 일반일정은 DB 수정 **후** 강사 조회·알림을 하므로 저장됐는데 오류가 날 수 있어,
 * 호출부가 재조회로 실제 상태를 판정할 수 있게 본문을 그대로 넘긴다.
 */
const modifyGeneralScheduleTimeAPI = async (
    centerId: string,
    onetimeScheduleId: string,
    body: ModifyGeneralScheduleTimeBody,
    accessToken: string
): Promise<ScheduleWriteResult> => {
    try {
        const response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/onetimeschedules/center/${centerId}/onetimeScheudle/${onetimeScheduleId}`,
            headers: { authorization: accessToken },
            data: {
                coachId: body.coachId,
                name: body.name,
                startTime: body.startTime,
                endTime: body.endTime,
            },
        });
        return toScheduleWriteResult(response);
    } catch (error: any) {
        return toScheduleWriteFailure(error);
    }
};

export /**
 * @method patch
 * @brief 일간/주간 강사스케줄 / 수업일정 수정
 */
const updateScheduleSessionAPI = async (
    coachId: string,
    memberId: string,
    scheduleId: string,
    currentSession: number,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v2/memberships/coach/${coachId}/member/${memberId}/currentsession`,
            headers: { authorization: accessToken },
            data: {
                scheduleId,
                currentSession,
            },
        });

        if (response.status === 200 || response.status === 210) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 412:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method patch
 * @brief 일간/주간 강사스케줄 / 수업일정 회원권 수정
 */
const modifyOneOnOneScheduleMembershipAPI = async (
    centerId: string,
    scheduleId: string,
    coachId: string,
    newMembershipId: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}/schedule/${scheduleId}/membership`,
            headers: { authorization: accessToken },
            data: {
                coachId,
                newMembershipId,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // 공유 수강권이 걸린 일정의 수강권 교체는 서버가 항상 409로 거절한다
        // (SHARED_MEMBERSHIP_TRANSFER_REQUIRES_DEDICATED_FLOW / SCHEDULE_MEMBERSHIP_CHANGED).
        // 분기하지 않은 상태코드를 undefined로 삼키면 호출부가 res.status에서 터져 무음 실패한다.
        return { status: error?.response?.status ?? 0, data: error?.response?.data };
    }
};

export /**
 * @method patch
 * @brief 일간 강사스케줄 / 일반일정 수정
 */
const modifyScheduleAttendance = async (
    scheduleId: string,
    attendanceStatus: string,
    accessToken: string,
    noShowNoticeType?: 'NOSHOW' | 'SAME_DAY_CANCEL'
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/${scheduleId}/attendancestatus`,
            headers: { authorization: accessToken },
            // 구서버는 모르는 키를 400 으로 거부한다. 노쇼로 바꾸면서 '당일 취소' 문구를 골랐을 때만 키를 싣는다.
            // 노쇼(기본)·출석 요청에는 키를 아예 넣지 않는다.
            data: {
                attendanceStatus,
                ...(attendanceStatus === 'NOSHOW' && noShowNoticeType === 'SAME_DAY_CANCEL'
                    ? { noShowNoticeType: 'SAME_DAY_CANCEL' }
                    : {}),
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 410:
                return { status: error.response.status };
            case 420:
                return { status: error.response.status };
            case 430:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method delete
 * @brief 일정삭제
 */
const deleteScheduleAPI = async (
    centerId: string,
    scheduleId: string,
    coachId: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'delete',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}/schedule/${scheduleId}`,
            headers: { authorization: accessToken },
            data: {
                coachId,
            },
        });

        if (response.status === 204) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };

            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method delete
 * @brief 일간 강사스케줄 / 일반일정 삭제
 */
const deleteGeneralScheduleAPI = async (
    centerId: string,
    onetimeScheudleId: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'delete',
            url: `${process.env.REACT_APP_SERVER}/v1/onetimeschedules/center/${centerId}/onetimeScheudle/${onetimeScheudleId}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 204) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };

            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method delete
 * @brief OFF 일정 삭제
 */
const deleteOffScheduleAPI = async (centerId: string, blockId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'delete',
            url: `${process.env.REACT_APP_SERVER}/v1/blocks/center/${centerId}/block/${blockId}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 204) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };

            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method patch
 * @brief 수업 신청 수락
 */
const approveScheduleRequestAPI = async (centerId: string, requestId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/requests/center/${centerId}/request/${requestId}/approve`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // 공유 수강권 신청 수락은 409(MEMBERSHIP_CAPACITY_EXHAUSTED / SHARED_MEMBERSHIP_POOL_CHANGED /
        // MEMBERSHIP_USAGE_BUSY)로 거절될 수 있다. 상태만 돌려주면 원인을 알려줄 수 없고,
        // 분기 없는 상태코드는 undefined가 되어 호출부가 '다시 등록해주세요'로 잘못 안내한다.
        return { status: error?.response?.status ?? 0, data: error?.response?.data };
    }
};

export /**
 * @method patch
 * @brief 수업 신청 거절
 */
const rejectScheduleRequestAPI = async (centerId: string, requestId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/requests/center/${centerId}/request/${requestId}/reject`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // 거절도 공유 풀 상태 변화(409)로 실패할 수 있어 수락과 같은 규칙으로 본문까지 넘긴다.
        return { status: error?.response?.status ?? 0, data: error?.response?.data };
    }
};

export /**
 * @method post
 * @brief 1:1 일정추가
 */
const createOneMoreScheduleAPI = async (
    centerId: string,
    coachId: string,
    memberId: string,
    startTime: string,
    endTime: string,
    membershipId: string,
    accessToken: string,
    sharedMembership?: SharedScheduleWriteContext,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/schedules/center/${centerId}/one-more-schedule`,
            headers: getScheduleWriteHeaders(accessToken, sharedMembership),
            data: {
                coachId,
                memberId,
                startTime,
                endTime,
                membershipId,
                ...(sharedMembership || {}),
            },
        });

        if (response.status === 201 || response.status === 200) {
            return response;
        }
        return response;
    } catch (error: any) {
        // Error 😨
        const status = error?.response?.status ?? 0;
        switch (status) {
            case 400:
                return { status, data: error?.response?.data };
            case 401:
                return { status, data: error?.response?.data };
            case 403:
                return { status, data: error?.response?.data };
            case 404:
                return { status, data: error?.response?.data };
            case 406:
                return { status, data: error?.response?.data };
            case 409:
                return { status, data: error?.response?.data };
            case 410:
                return { status, data: error?.response?.data };
            case 420:
                return { status, data: error?.response?.data };
            case 430:
                return { status, data: error?.response?.data };
            case 500:
                alert('서버 내부 오류.');
                return { status, data: error?.response?.data };
            default:
                return { status, data: error?.response?.data };
        }
    }
};
